/**
 * BoxenBaas Favorites — client-side storage module (v3)
 * ============================================================
 * Storage in localStorage onder de key 'bb_favorites'.
 *
 * v3 (vanaf 2026-04-26): composite key — 1 favoriet = (handle + variantId).
 * Hetzelfde product kan meerdere keren in de lijst staan, één entry per
 * variant. Voor B2B essentieel: "Papieren tape Bruin/48" en "Wit/75" zijn
 * verschillende artikelen.
 *
 * Format v3:
 *   [
 *     { "handle": "papieren-tape", "variantId": 57090834825599 },
 *     { "handle": "papieren-tape", "variantId": 57090834858367 },
 *     { "handle": "gripzakje-50-micron", "variantId": 60123456789 }
 *   ]
 *
 * Format v2 (legacy, blijft lezen):
 *   [{ handle, variantId? }] — entries zonder variantId krijgen
 *   variantId=null bij read; UI moet auto-migratie naar eerste-beschikbare
 *   variant doen via setVariantId() na fetch.
 *
 * Format v1 (legacy, blijft lezen):
 *   [ "papieren-tape", ... ] — strings worden { handle, variantId: null }
 *
 * Public API (window.BBFavorites):
 *   - add(handle, variantId)              → toevoegen (uniek op handle+variantId)
 *   - remove(handle, variantId)           → verwijder die exacte combo
 *   - removeAll(handle)                   → verwijder alle varianten van product
 *   - toggle(handle, variantId)           → toggle die exacte combo
 *   - has(handle, variantId)              → boolean (exacte combo)
 *   - hasAnyVariant(handle)               → boolean (welke variant dan ook)
 *   - count()                             → aantal entries
 *   - countForHandle(handle)              → aantal varianten van dit product
 *   - getAll()                            → Array<{handle, variantId}>
 *   - getVariantsForHandle(handle)        → Array<variantId>
 *   - setVariantId(handle, oldVid, newVid)→ wijzig één entry's variant
 *   - clear()                             → leegmaken
 *
 * UI-bindings (auto):
 *   - [data-bb-fav][data-bb-fav-handle="…"] knoppen
 *     → optioneel data-bb-fav-variant="..." voor exacte combo
 *     → zonder variant: visueel actief als hasAnyVariant(handle), klik = no-op
 *       (laat bubblen naar parent <a> voor PDP-navigatie)
 *     → met variant: toggle exacte combo
 *   - .is-active class hydrateert
 *   - [data-bb-fav-link] in header → '.has-favorites' bij count > 0
 *   - [data-bb-fav-count] → tekst + 'hidden' attr
 *   - .is-animating heart-beat bij elke add
 *
 * Cross-tab sync: storage-event listener.
 * ============================================================
 */
(function () {
  'use strict';

  var STORAGE_KEY = 'bb_favorites';
  var EVENT_NAME = 'bb:favorites:changed';
  var ANIMATION_MS = 400;

  // ---------- Storage helpers ----------
  function readStorage() {
    try {
      var raw = window.localStorage.getItem(STORAGE_KEY);
      if (!raw) return [];
      var parsed = JSON.parse(raw);
      if (!Array.isArray(parsed)) return [];

      // Normaliseer naar v3 (composite key, dedupe)
      var seen = Object.create(null);
      var result = [];
      for (var i = 0; i < parsed.length; i++) {
        var entry = parsed[i];
        var handle = null;
        var variantId = null;
        if (typeof entry === 'string' && entry.length > 0) {
          handle = entry;
        } else if (entry && typeof entry === 'object' && typeof entry.handle === 'string' && entry.handle.length > 0) {
          handle = entry.handle;
          if (entry.variantId != null && entry.variantId !== '') {
            var n = parseInt(entry.variantId, 10);
            variantId = isFinite(n) ? n : null;
          }
        }
        if (!handle) continue;
        var key = handle + '|' + (variantId == null ? 'NULL' : variantId);
        if (seen[key]) continue;
        seen[key] = true;
        result.push({ handle: handle, variantId: variantId });
      }
      return result;
    } catch (e) {
      return [];
    }
  }

  function writeStorage(list) {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(list));
    } catch (e) {}
  }

  function normVariantId(v) {
    if (v == null || v === '') return null;
    var n = parseInt(v, 10);
    return isFinite(n) ? n : null;
  }

  // ---------- Core API ----------
  function getAll() {
    return readStorage().map(function (e) {
      return { handle: e.handle, variantId: e.variantId };
    });
  }

  function getVariantsForHandle(handle) {
    if (!handle) return [];
    var list = readStorage();
    var out = [];
    for (var i = 0; i < list.length; i++) {
      if (list[i].handle === handle && list[i].variantId != null) {
        out.push(list[i].variantId);
      }
    }
    return out;
  }

  function has(handle, variantId) {
    if (!handle) return false;
    var vid = normVariantId(variantId);
    var list = readStorage();
    for (var i = 0; i < list.length; i++) {
      if (list[i].handle === handle && list[i].variantId === vid) return true;
    }
    return false;
  }

  function hasAnyVariant(handle) {
    if (!handle) return false;
    var list = readStorage();
    for (var i = 0; i < list.length; i++) {
      if (list[i].handle === handle) return true;
    }
    return false;
  }

  function count() {
    return readStorage().length;
  }

  function countForHandle(handle) {
    if (!handle) return 0;
    var list = readStorage();
    var c = 0;
    for (var i = 0; i < list.length; i++) {
      if (list[i].handle === handle) c++;
    }
    return c;
  }

  function add(handle, variantId) {
    if (!handle) return false;
    var vid = normVariantId(variantId);
    var list = readStorage();
    for (var i = 0; i < list.length; i++) {
      if (list[i].handle === handle && list[i].variantId === vid) return false;
    }
    list.push({ handle: handle, variantId: vid });
    writeStorage(list);
    fireChange({ action: 'add', handle: handle, variantId: vid });
    return true;
  }

  function remove(handle, variantId) {
    if (!handle) return false;
    var vid = normVariantId(variantId);
    var list = readStorage();
    var newList = [];
    var removed = false;
    for (var i = 0; i < list.length; i++) {
      if (list[i].handle === handle && list[i].variantId === vid) { removed = true; continue; }
      newList.push(list[i]);
    }
    if (!removed) return false;
    writeStorage(newList);
    fireChange({ action: 'remove', handle: handle, variantId: vid });
    return true;
  }

  function removeAll(handle) {
    if (!handle) return false;
    var list = readStorage();
    var newList = [];
    var removed = 0;
    for (var i = 0; i < list.length; i++) {
      if (list[i].handle === handle) { removed++; continue; }
      newList.push(list[i]);
    }
    if (!removed) return false;
    writeStorage(newList);
    fireChange({ action: 'remove-all', handle: handle, count: removed });
    return true;
  }

  function toggle(handle, variantId) {
    if (!handle) return false;
    var vid = normVariantId(variantId);
    if (has(handle, vid)) {
      remove(handle, vid);
      return false;
    }
    add(handle, vid);
    return true;
  }

  function setVariantId(handle, oldVariantId, newVariantId) {
    if (!handle) return false;
    var oldVid = normVariantId(oldVariantId);
    var newVid = normVariantId(newVariantId);
    if (oldVid === newVid) return true;
    var list = readStorage();
    var matched = false;
    var clashed = false;
    for (var i = 0; i < list.length; i++) {
      if (list[i].handle === handle && list[i].variantId === newVid) clashed = true;
      if (list[i].handle === handle && list[i].variantId === oldVid && !matched) {
        list[i].variantId = newVid;
        matched = true;
      }
    }
    if (clashed) {
      // Combo bestond al — verwijder oude entry zodat we niet duplicate hebben
      var dedup = [];
      var firstSeen = false;
      for (var j = 0; j < list.length; j++) {
        if (list[j].handle === handle && list[j].variantId === newVid) {
          if (firstSeen) continue;
          firstSeen = true;
        }
        dedup.push(list[j]);
      }
      list = dedup;
    }
    if (!matched) return false;
    writeStorage(list);
    fireChange({ action: 'update', handle: handle, oldVariantId: oldVid, variantId: newVid });
    return true;
  }

  function clearAll() {
    writeStorage([]);
    fireChange({ action: 'clear' });
  }

  function fireChange(detail) {
    try {
      var ev = new CustomEvent(EVENT_NAME, { detail: detail });
      document.dispatchEvent(ev);
    } catch (e) {}
  }

  // ---------- UI binding: hartje-knoppen ----------
  function syncFavButton(btn) {
    var handle = btn.getAttribute('data-bb-fav-handle');
    if (!handle) return;
    var variantAttr = btn.getAttribute('data-bb-fav-variant');
    var active;
    if (variantAttr != null && variantAttr !== '') {
      active = has(handle, variantAttr);
    } else {
      active = hasAnyVariant(handle);
    }
    btn.classList.toggle('is-active', active);
    btn.setAttribute('aria-pressed', active ? 'true' : 'false');
    btn.setAttribute(
      'aria-label',
      active ? 'In favorieten — klik door naar product om te wijzigen' : 'Toevoegen aan favorieten'
    );
  }

  function syncAllFavButtons() {
    var btns = document.querySelectorAll('[data-bb-fav][data-bb-fav-handle]');
    for (var i = 0; i < btns.length; i++) syncFavButton(btns[i]);
  }

  function onFavClick(e) {
    var btn = e.target.closest && e.target.closest('[data-bb-fav][data-bb-fav-handle]');
    if (!btn) return;
    var handle = btn.getAttribute('data-bb-fav-handle');
    if (!handle) return;
    var variantAttr = btn.getAttribute('data-bb-fav-variant');

    // Mode 1: knop heeft geen variant → behaviour bepaald door data-bb-fav-mode
    //   - "navigate" (default): laat klik bubblen naar parent <a>; doe niets
    //   - anders (legacy): voorkom default + gewoon stoppen
    if (variantAttr == null || variantAttr === '') {
      var mode = btn.getAttribute('data-bb-fav-mode') || 'navigate';
      if (mode === 'navigate') {
        // Vind parent <a> en navigeer ernaartoe
        var card = btn.closest('article, .bb-aanv-v4-card, .bb-product-v4');
        var link = card && card.querySelector('a[href]');
        if (link && link.href) {
          e.preventDefault();
          e.stopPropagation();
          window.location.href = link.href;
          return;
        }
        // Geen link gevonden — laat klik gewoon doorgaan (parent kan link zijn)
        return;
      }
      e.preventDefault();
      e.stopPropagation();
      return;
    }

    // Mode 2: knop heeft variant → toggle exacte combo
    e.preventDefault();
    e.stopPropagation();
    toggle(handle, variantAttr);
  }

  // ---------- UI binding: header-counter + state ----------
  function updateHeaderState(detail) {
    var c = count();
    var countEls = document.querySelectorAll('[data-bb-fav-count]');
    for (var i = 0; i < countEls.length; i++) {
      var el = countEls[i];
      el.textContent = String(c);
      if (c > 0) el.removeAttribute('hidden'); else el.setAttribute('hidden', 'hidden');
    }

    var links = document.querySelectorAll('[data-bb-fav-link]');
    for (var j = 0; j < links.length; j++) {
      links[j].classList.toggle('has-favorites', c > 0);
      if (detail && detail.action === 'add') {
        links[j].classList.remove('is-animating');
        // eslint-disable-next-line no-unused-expressions
        links[j].offsetWidth;
        links[j].classList.add('is-animating');
        (function (el) {
          window.setTimeout(function () { el.classList.remove('is-animating'); }, ANIMATION_MS);
        })(links[j]);
      }
    }
  }

  // ---------- Cross-tab sync ----------
  function onStorageEvent(e) {
    if (e.key !== STORAGE_KEY) return;
    fireChange({ action: 'sync', source: 'storage-event' });
  }

  // ---------- PDP auto-injector ----------
  // Op /products/* — zoek h1.bb-title en injecteer hartje als die nog niet bestaat.
  // Werkt op alle GemPages-templates zonder per-section edits.
  function injectPdpFav() {
    var path = window.location.pathname || '';
    if (path.indexOf('/products/') !== 0 && path.indexOf('/products/') < 0) {
      // Probeer ook patterns zoals /collections/X/products/Y
    }
    if (!/\/products\//.test(path)) return;

    // Bestaat er al een PDP-fav-button?
    if (document.querySelector('.bb-pmain-fav[data-bb-fav-handle]')) return;

    // Vind de h1 (titel)
    var h1 = document.querySelector('h1.bb-title') || document.querySelector('main h1') || document.querySelector('h1');
    if (!h1) return;

    // Lees handle uit URL: /products/<handle>(?...)?
    var m = path.match(/\/products\/([^/?#]+)/);
    if (!m) return;
    var handle = m[1];

    // Zoek geselecteerde variant
    var radios = document.querySelectorAll('input[name="bb-variant-id"], input[name="id"][type="radio"]');
    var checked = null;
    for (var i = 0; i < radios.length; i++) {
      if (radios[i].checked) { checked = radios[i]; break; }
    }
    var initialVariant = checked ? checked.value : '';
    if (!initialVariant) {
      // Fallback: ?variant=… in URL
      try {
        var url = new URL(window.location.href);
        var v = url.searchParams.get('variant');
        if (v) initialVariant = v;
      } catch (e) {}
    }

    // Maak wrapper + button
    var wrap = document.createElement('div');
    wrap.className = 'bb-title-row bb-title-row--injected';
    var titleParent = h1.parentNode;
    titleParent.insertBefore(wrap, h1);
    wrap.appendChild(h1);

    var btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'bb-pmain-fav';
    btn.setAttribute('data-bb-fav', '');
    btn.setAttribute('data-bb-fav-handle', handle);
    if (initialVariant) btn.setAttribute('data-bb-fav-variant', initialVariant);
    btn.setAttribute('aria-label', 'Toevoegen aan favorieten');
    btn.innerHTML = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M6 3h12a1 1 0 0 1 1 1v17l-7-4-7 4V4a1 1 0 0 1 1-1z"/></svg>';
    wrap.appendChild(btn);

    // Inject scoped CSS (eenmalig)
    if (!document.getElementById('bb-pmain-fav-styles')) {
      var style = document.createElement('style');
      style.id = 'bb-pmain-fav-styles';
      style.textContent = ''
        + '.bb-title-row{display:flex;align-items:flex-start;justify-content:space-between;gap:14px;margin:0 0 12px;}'
        + '.bb-title-row .bb-title{margin:0 !important;flex:1 1 auto;min-width:0;}'
        + '.bb-pmain-fav{flex:0 0 auto;appearance:none;width:44px;height:44px;border-radius:12px;background:rgba(255,255,255,.92);border:1px solid #d8e5f0;display:flex;align-items:center;justify-content:center;color:#94a3b8;cursor:pointer;padding:0;margin-top:6px;box-shadow:0 6px 14px rgba(15,23,42,.06);transition:color .15s,background .15s,transform .15s,border-color .15s,box-shadow .15s;}'
        + '.bb-pmain-fav:hover{color:#f59e0b;border-color:#fed7aa;transform:scale(1.06);box-shadow:0 10px 22px rgba(245,158,11,.18);}'
        + '.bb-pmain-fav.is-active{color:#f59e0b;background:#fff7ed;border-color:#fed7aa;box-shadow:0 8px 18px rgba(245,158,11,.22);}'
        + '.bb-pmain-fav.is-active svg{fill:#f59e0b;}'
        + '.bb-pmain-fav svg{width:22px;height:22px;display:block;}'
        + '.bb-pmain-fav:focus-visible{outline:none;box-shadow:0 0 0 3px rgba(255,255,255,.95), 0 0 0 6px rgba(245,158,11,.30);}'
        + '@media (max-width:767px){.bb-title-row{gap:10px;}.bb-pmain-fav{width:40px;height:40px;border-radius:10px;}.bb-pmain-fav svg{width:20px;height:20px;}}';
      document.head.appendChild(style);
    }

    // Initieel syncen
    syncFavButton(btn);

    // Listen op variant-radio change → update data-bb-fav-variant
    function onVariantChange() {
      var radios = document.querySelectorAll('input[name="bb-variant-id"], input[name="id"][type="radio"]');
      for (var i = 0; i < radios.length; i++) {
        if (radios[i].checked) {
          btn.setAttribute('data-bb-fav-variant', radios[i].value);
          syncFavButton(btn);
          return;
        }
      }
    }
    document.querySelectorAll('input[name="bb-variant-id"], input[name="id"][type="radio"]').forEach(function (r) {
      r.addEventListener('change', onVariantChange);
    });
  }

  // ---------- Init ----------
  function init() {
    injectPdpFav();
    syncAllFavButtons();
    updateHeaderState();
    document.addEventListener('click', onFavClick);
    document.addEventListener(EVENT_NAME, function (e) {
      syncAllFavButtons();
      updateHeaderState(e.detail);
    });
    window.addEventListener('storage', onStorageEvent);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // ---------- Expose ----------
  window.BBFavorites = {
    add: add,
    remove: remove,
    removeAll: removeAll,
    toggle: toggle,
    has: has,
    hasAnyVariant: hasAnyVariant,
    count: count,
    countForHandle: countForHandle,
    getAll: getAll,
    getVariantsForHandle: getVariantsForHandle,
    setVariantId: setVariantId,
    clear: clearAll,
    EVENT_NAME: EVENT_NAME,
    STORAGE_KEY: STORAGE_KEY,
    VERSION: 3
  };
})();
